var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { brand } from './brand';
import { EspLogo, TopoBackground } from './icons';
import packageJson from '../../package.json';
/**
 * AuthScaffold — the shared chrome for every sign-in screen: dark topographic
 * backdrop, centered frosted-glass card, ESP brand header, and a version
 * footer. Per-screen content is passed as children.
 * @param root0
 * @param root0.children
 */
export function AuthScaffold(_a) {
    var children = _a.children;
    return (_jsxs(Box, { sx: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: brand.bg,
            px: 2
        }, children: [_jsx(TopoBackground, {}), _jsx(Paper, { elevation: 0, sx: {
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    maxWidth: 380,
                    px: { xs: 3, sm: 4 },
                    py: { xs: 3.5, sm: 4.5 },
                    borderRadius: '18px',
                    bgcolor: brand.card,
                    border: "1px solid ".concat(brand.cardBorder),
                    boxShadow: brand.cardShadow,
                    backdropFilter: 'blur(14px)',
                    WebkitBackdropFilter: 'blur(14px)'
                }, children: _jsxs(Stack, { spacing: 3, children: [_jsxs(Stack, { spacing: 1.25, alignItems: "center", children: [_jsx(EspLogo, {}), _jsxs(Box, { sx: { textAlign: 'center' }, children: [_jsx(Typography, { component: "div", sx: {
                                                fontWeight: 800,
                                                fontSize: '1.4rem',
                                                letterSpacing: '0.3em',
                                                color: brand.textPrimary,
                                                // nudge to optically center letter-spaced text
                                                pl: '0.3em',
                                                lineHeight: 1.1
                                            }, children: 'ESP' }), _jsx(Typography, { component: "div", sx: {
                                                fontFamily: brand.mono,
                                                fontSize: '0.66rem',
                                                letterSpacing: '0.34em',
                                                color: brand.textMuted,
                                                pl: '0.34em',
                                                mt: 0.75
                                            }, children: 'LOGISTICS · TECHNOLOGY' })] })] }), children] }) }), _jsx(Typography, { component: "div", sx: {
                    position: 'absolute',
                    bottom: 18,
                    left: 0,
                    right: 0,
                    textAlign: 'center',
                    fontFamily: brand.mono,
                    fontSize: '0.62rem',
                    letterSpacing: '0.18em',
                    color: brand.textMuted,
                    opacity: 0.7,
                    zIndex: 1
                }, children: "v".concat(packageJson.version) })] }));
}
/**
 * Left-aligned "Back" link shown above OTP screen headings.
 * @param root0
 * @param root0.onClick
 */
export function BackLink(_a) {
    var onClick = _a.onClick;
    return (_jsxs(Box, { role: "button", tabIndex: 0, onClick: onClick, onKeyDown: function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                onClick();
            }
        }, sx: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            alignSelf: 'flex-start',
            cursor: 'pointer',
            color: brand.textSecondary,
            fontSize: '0.85rem',
            '&:hover': { color: brand.textPrimary }
        }, children: [_jsx(ArrowBackIosNewIcon, { sx: { fontSize: '0.8rem' } }), 'Back'] }));
}
/**
 * Screen heading + supporting copy.
 * @param root0
 * @param root0.title
 * @param root0.subtitle
 */
export function ScreenHeading(_a) {
    var title = _a.title, subtitle = _a.subtitle;
    return (_jsxs(Box, { sx: { textAlign: 'center' }, children: [_jsx(Typography, { component: "h1", sx: {
                    fontWeight: 700,
                    fontSize: '1.35rem',
                    color: brand.textPrimary
                }, children: title }), subtitle && (_jsx(Typography, { component: "div", sx: {
                    mt: 0.75,
                    fontSize: '0.9rem',
                    color: brand.textSecondary
                }, children: subtitle }))] }));
}
/**
 * Filled accent button — the primary call to action on each screen.
 * @param props
 */
export function PrimaryButton(props) {
    var sx = props.sx, rest = __rest(props, ["sx"]);
    return (_jsx(Button, __assign({ fullWidth: true, disableElevation: true, variant: "contained", sx: __assign({ py: 1.25, borderRadius: '10px', textTransform: 'none', fontSize: '0.95rem', fontWeight: 600, color: brand.onPrimary, bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryHover }, '&.Mui-disabled': {
                bgcolor: brand.field,
                color: brand.textMuted
            } }, sx) }, rest)));
}
/**
 * Outlined dark button — secondary providers (Microsoft / SSO / code).
 * @param props
 */
export function SecondaryButton(props) {
    var sx = props.sx, rest = __rest(props, ["sx"]);
    return (_jsx(Button, __assign({ fullWidth: true, disableElevation: true, variant: "outlined", sx: __assign({ py: 1.2, borderRadius: '10px', textTransform: 'none', fontSize: '0.92rem', fontWeight: 500, color: brand.textPrimary, bgcolor: brand.field, borderColor: brand.fieldBorder, justifyContent: 'center', gap: 1, '&:hover': {
                bgcolor: brand.fieldHover,
                borderColor: brand.fieldBorderHover
            } }, sx) }, rest)));
}
